// Helper to render the Bill component to a PDF blob and optionally upload it.
// Exports:
// - renderBillToBlob(props) -> Promise<Blob>
// - renderAndUploadBill(props) -> Promise<string|null> (uploaded URL or null)
import React from 'react';
import ReactDOM from 'react-dom/client';
import Bill, { generateBillPdf, uploadFile } from '../components/Bill';

export async function renderBillToBlob({
  appointmentData = {},
  selectedServices = [],
  billSummary = {},
  billPaymentNowSelection = null,
  paymentState = null,
  paidAmount = 0,
  pendingAmount = 0,
  refundAmount = 0,
  fileName = null,
  nodeId = "bill-preview",
} = {}) {
  // Use statically imported generateBillPdf from Bill component
  if (!generateBillPdf || typeof generateBillPdf !== "function") {
    throw new Error("generateBillPdf not available from Bill component module");
  }

  // Render the Bill component into an offscreen container so generateBillPdf can pick it up
  const tempContainer = document.createElement("div");
  tempContainer.style.position = "absolute";
  tempContainer.style.left = "-9999px";
  tempContainer.id = "temp-bill-render";
  document.body.appendChild(tempContainer);

  let root;
  try {
    // Use the statically imported ReactDOM
    root = ReactDOM.createRoot(tempContainer);
    root.render(
      React.createElement(Bill, {
        appointmentData,
        selectedServices,
        billSummary,
        billPaymentNowSelection,
        paymentState,
        paidAmount,
        pendingAmount,
        refundAmount,
      }),
    );

    // small delay for render to settle (fonts/images minimal in Bill)
    await new Promise((res) => setTimeout(res, 300));

    // generate PDF blob
    const blob = await generateBillPdf(nodeId, false, fileName);
    return blob;
  } finally {
    // cleanup
    try {
      if (root && root.unmount) root.unmount();
    } catch (e) {
      // ignore
    }
    if (tempContainer && tempContainer.parentNode) tempContainer.parentNode.removeChild(tempContainer);
  }
}

export async function renderAndUploadBill(opts = {}) {
  const { fileName } = opts;
  const blob = await renderBillToBlob(opts);
  if (!blob) return null;
  // Use statically imported uploadFile from Bill component
  if (!uploadFile || typeof uploadFile !== "function") {
    throw new Error("uploadFile not available from Bill component module");
  }
  const uploadedUrl = await uploadFile(blob, {
    fileName: fileName || `bill_${Date.now()}.pdf`,
    mimeType: "application/pdf",
  });
  return uploadedUrl || null;
}

export default {
  renderBillToBlob,
  renderAndUploadBill,
};
